import { useState } from 'react';
import { 
  Plus, Trash2, GripVertical, 
  Type, Mail, Phone, ChevronDown, ToggleLeft, 
  ToggleRight, Edit2, X, Check, FileText,
  AlertCircle, Calendar, Hash, Globe,
  MapPin, Map, User, Building, CreditCard
} from 'lucide-react';

const FormBuilder = ({ formSettings, onChange }) => {
  const [isFormActive, setIsFormActive] = useState(formSettings?.active || false);
  const [formInputs, setFormInputs] = useState(formSettings?.inputs || []);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddInput, setShowAddInput] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [newInput, setNewInput] = useState({
    type: 'text',
    name: '',
    label: '',
    placeholder: '',
    required: false,
    options: []
  });

  // Tipos de campo disponíveis com configurações automáticas
  const inputTypes = [
    { 
      value: 'text', 
      label: 'Texto', 
      icon: Type,
      defaultName: 'texto',
      defaultLabel: 'Texto',
      defaultPlaceholder: 'Digite aqui'
    },
    { 
      value: 'name', 
      label: 'Nome', 
      icon: User,
      defaultName: 'nome',
      defaultLabel: 'Nome Completo',
      defaultPlaceholder: 'Digite seu nome completo'
    },
    { 
      value: 'email', 
      label: 'E-mail', 
      icon: Mail,
      defaultName: 'email',
      defaultLabel: 'E-mail',
      defaultPlaceholder: 'seu@email.com'
    },
    { 
      value: 'phone', 
      label: 'Telefone', 
      icon: Phone,
      defaultName: 'telefone',
      defaultLabel: 'Telefone',
      defaultPlaceholder: '(11) 99999-9999'
    },
    { 
      value: 'company', 
      label: 'Empresa', 
      icon: Building,
      defaultName: 'empresa',
      defaultLabel: 'Empresa',
      defaultPlaceholder: 'Nome da empresa'
    },
    { 
      value: 'cpf', 
      label: 'CPF', 
      icon: CreditCard,
      defaultName: 'cpf',
      defaultLabel: 'CPF',
      defaultPlaceholder: '000.000.000-00'
    },
    { 
      value: 'state', 
      label: 'Estado', 
      icon: Map,
      defaultName: 'estado',
      defaultLabel: 'Estado',
      defaultPlaceholder: 'Selecione o estado',
      useDynamicLoad: true
    },
    { 
      value: 'city', 
      label: 'Cidade', 
      icon: MapPin,
      defaultName: 'cidade',
      defaultLabel: 'Cidade',
      defaultPlaceholder: 'Selecione a cidade',
      useDynamicLoad: true
    },
    { 
      value: 'dropdown', 
      label: 'Lista Personalizada', 
      icon: ChevronDown,
      defaultName: 'opcao',
      defaultLabel: 'Selecione',
      defaultPlaceholder: 'Escolha uma opção'
    },
    { 
      value: 'number', 
      label: 'Número', 
      icon: Hash,
      defaultName: 'numero',
      defaultLabel: 'Número',
      defaultPlaceholder: '0'
    },
    { 
      value: 'date', 
      label: 'Data', 
      icon: Calendar,
      defaultName: 'data',
      defaultLabel: 'Data',
      defaultPlaceholder: 'Selecione a data'
    },
    { 
      value: 'url', 
      label: 'Site', 
      icon: Globe,
      defaultName: 'site',
      defaultLabel: 'Site',
      defaultPlaceholder: 'https://exemplo.com'
    }
  ];

  // Campos rápidos mais comuns
  const quickFields = [
    { type: 'name', icon: User, label: 'Nome' },
    { type: 'email', icon: Mail, label: 'E-mail' },
    { type: 'phone', icon: Phone, label: 'Telefone' },
    { type: 'company', icon: Building, label: 'Empresa' },
    { type: 'state', icon: Map, label: 'Estado' },
    { type: 'city', icon: MapPin, label: 'Cidade' }
  ];

  // Ícone do tipo de campo
  const getFieldIcon = (type) => {
    const fieldType = inputTypes.find(t => t.value === type || (t.isDropdown && type === 'dropdown'));
    const Icon = fieldType?.icon || Type;
    return <Icon className="w-4 h-4" />;
  };

  // Atualizar configurações do formulário
  const updateFormSettings = (active, inputs) => {
    const newSettings = {
      active,
      inputs
    };
    onChange(newSettings);
  };

  // Toggle formulário ativo/inativo
  const toggleFormActive = () => {
    const newActive = !isFormActive;
    setIsFormActive(newActive);
    updateFormSettings(newActive, formInputs);
  };

  // Adicionar campo rápido (simplificado)
  const handleQuickAdd = (fieldType) => {
    const typeConfig = inputTypes.find(t => t.value === fieldType);
    if (!typeConfig) return;

    // Gerar nome único se já existir
    let baseName = typeConfig.defaultName;
    let counter = 1;
    let finalName = baseName;
    
    while (formInputs.some(input => input.name === finalName)) {
      finalName = `${baseName}_${counter}`;
      counter++;
    }

    const newField = {
      type: typeConfig.isDropdown ? 'dropdown' : fieldType,
      name: finalName,
      label: typeConfig.defaultLabel,
      placeholder: typeConfig.defaultPlaceholder,
      required: false,
      options: typeConfig.presetOptions || []
    };

    const updatedInputs = [...formInputs, newField];
    setFormInputs(updatedInputs);
    updateFormSettings(isFormActive, updatedInputs);
  };

  // Adicionar novo campo (com configurações avançadas)
  const handleAddInput = () => {
    if (!showAdvancedSettings) {
      // Modo simplificado - usar valores padrão
      const typeConfig = inputTypes.find(t => t.value === newInput.type);
      
      // Gerar nome único
      let baseName = typeConfig?.defaultName || 'campo';
      let counter = 1;
      let finalName = baseName;
      
      while (formInputs.some(input => input.name === finalName)) {
        finalName = `${baseName}_${counter}`;
        counter++;
      }

      const inputToAdd = {
        type: typeConfig?.isDropdown ? 'dropdown' : newInput.type,
        name: finalName,
        label: typeConfig?.defaultLabel || 'Campo',
        placeholder: typeConfig?.defaultPlaceholder || '',
        required: newInput.required,
        options: typeConfig?.presetOptions || []
      };

      const updatedInputs = [...formInputs, inputToAdd];
      setFormInputs(updatedInputs);
      updateFormSettings(isFormActive, updatedInputs);
    } else {
      // Modo avançado - validar campos
      if (!newInput.name || !newInput.label) {
        alert('Nome e Label são obrigatórios');
        return;
      }

      if (formInputs.some(input => input.name === newInput.name)) {
        alert('Já existe um campo com esse nome');
        return;
      }

      const typeConfig = inputTypes.find(t => t.value === newInput.type);
      const inputToAdd = {
        ...newInput,
        type: typeConfig?.isDropdown ? 'dropdown' : newInput.type,
        placeholder: newInput.placeholder || newInput.label,
        options: typeConfig?.presetOptions || newInput.options || []
      };

      const updatedInputs = [...formInputs, inputToAdd];
      setFormInputs(updatedInputs);
      updateFormSettings(isFormActive, updatedInputs);
    }
    
    // Reset form
    setNewInput({
      type: 'text',
      name: '',
      label: '',
      placeholder: '',
      required: false,
      options: []
    });
    setShowAddInput(false);
    setShowAdvancedSettings(false);
  };

  // Remover campo
  const handleRemoveInput = (index) => {
    const updatedInputs = formInputs.filter((_, i) => i !== index);
    setFormInputs(updatedInputs);
    updateFormSettings(isFormActive, updatedInputs);
  };

  // Atualizar campo existente
  const handleUpdateInput = (index, field, value) => {
    const updatedInputs = [...formInputs];
    updatedInputs[index] = {
      ...updatedInputs[index],
      [field]: value
    };
    setFormInputs(updatedInputs);
    updateFormSettings(isFormActive, updatedInputs);
  };

  // Reordenar campos
  const moveInput = (fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= formInputs.length) return;

    const updatedInputs = [...formInputs];
    const [movedItem] = updatedInputs.splice(fromIndex, 1);
    updatedInputs.splice(toIndex, 0, movedItem);
    
    setFormInputs(updatedInputs);
    updateFormSettings(isFormActive, updatedInputs);
  };

  // Adicionar opção ao dropdown personalizado
  const addDropdownOption = () => {
    setNewInput({
      ...newInput,
      options: [...(newInput.options || []), { value: '', label: '' }]
    });
  };

  // Atualizar opção do dropdown
  const updateDropdownOption = (index, field, value) => {
    const updatedOptions = [...(newInput.options || [])];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value
    };
    setNewInput({
      ...newInput,
      options: updatedOptions
    });
  };

  // Remover opção do dropdown
  const removeDropdownOption = (index) => {
    const updatedOptions = (newInput.options || []).filter((_, i) => i !== index);
    setNewInput({
      ...newInput,
      options: updatedOptions
    });
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-8 h-8 text-green-600" />
          Formulário de Atendimento
        </h3>
        
        <button
          onClick={toggleFormActive}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
            isFormActive 
              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {isFormActive ? (
            <ToggleRight className="w-6 h-6" />
          ) : (
            <ToggleLeft className="w-6 h-6" />
          )}
          <span className="font-semibold">
            {isFormActive ? 'Ativado' : 'Desativado'}
          </span>
        </button>
      </div>

      {/* Descrição */}
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
        <p className="text-base text-gray-700 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          Configure os campos do formulário que será exibido antes de iniciar a conversa. 
          Clique nos botões rápidos para adicionar campos comuns.
        </p>
      </div>

      {isFormActive && (
        <>
          {/* Botões de adição rápida */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Adicionar campo rápido:</p>
            <div className="flex flex-wrap gap-2">
              {quickFields.map(field => (
                <button
                  key={field.type}
                  onClick={() => handleQuickAdd(field.type)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-200"
                >
                  <field.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{field.label}</span>
                  <Plus className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>

          {/* Lista de campos existentes */}
          <div className="space-y-3 mb-6">
            {formInputs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <Type className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Nenhum campo configurado</p>
                <p className="text-sm text-gray-500 mt-1">Use os botões rápidos acima ou adicione campos personalizados</p>
              </div>
            ) : (
              formInputs.map((input, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  {editingIndex === index ? (
                    // Modo de edição
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={input.label}
                          onChange={(e) => handleUpdateInput(index, 'label', e.target.value)}
                          className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                          placeholder="Label"
                        />
                        <input
                          type="text"
                          value={input.name}
                          onChange={(e) => handleUpdateInput(index, 'name', e.target.value)}
                          className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                          placeholder="Nome do campo"
                        />
                      </div>
                      <input
                        type="text"
                        value={input.placeholder}
                        onChange={(e) => handleUpdateInput(index, 'placeholder', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-green-500"
                        placeholder="Placeholder"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingIndex(null)}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualização
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button className="text-gray-400 hover:text-gray-600 cursor-move">
                          <GripVertical className="w-5 h-5" />
                        </button>
                        
                        <div className="flex items-center gap-2 text-gray-600">
                          {getFieldIcon(input.type)}
                        </div>
                        
                        <div>
                          <p className="font-semibold text-gray-900">
                            {input.label}
                            {input.required && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          <p className="text-sm text-gray-500">
                            {input.type === 'dropdown' 
                              ? `Lista com ${input.options?.length || 0} opções` 
                              : `Campo: ${input.name}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Toggle obrigatório */}
                        <button
                          onClick={() => handleUpdateInput(index, 'required', !input.required)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            input.required 
                              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {input.required ? 'Obrigatório' : 'Opcional'}
                        </button>
                        
                        {/* Mover para cima */}
                        <button
                          onClick={() => moveInput(index, 'up')}
                          disabled={index === 0}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mover para cima"
                        >
                          ↑
                        </button>
                        
                        {/* Mover para baixo */}
                        <button
                          onClick={() => moveInput(index, 'down')}
                          disabled={index === formInputs.length - 1}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mover para baixo"
                        >
                          ↓
                        </button>
                        
                        {/* Editar */}
                        <button
                          onClick={() => setEditingIndex(index)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        {/* Remover */}
                        <button
                          onClick={() => handleRemoveInput(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Adicionar novo campo personalizado */}
          {showAddInput ? (
            <div className="bg-indigo-50 rounded-xl p-6 border-2 border-indigo-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Adicionar Campo Personalizado</h4>
                <button
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  {showAdvancedSettings ? 'Modo Simples' : 'Configurações Avançadas'}
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Seletor de tipo simplificado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Campo
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {inputTypes.map(type => (
                      <button
                        key={type.value}
                        onClick={() => setNewInput({ ...newInput, type: type.value, options: type.presetOptions || [] })}
                        className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                          newInput.type === type.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <type.icon className="w-5 h-5 text-gray-600" />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Configurações avançadas (opcional) */}
                {showAdvancedSettings && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Nome do campo (ID) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nome do Campo <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newInput.name}
                          onChange={(e) => setNewInput({ ...newInput, name: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="ex: nome_completo"
                        />
                      </div>
                      
                      {/* Label */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Label <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newInput.label}
                          onChange={(e) => setNewInput({ ...newInput, label: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="ex: Nome Completo"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Placeholder
                      </label>
                      <input
                        type="text"
                        value={newInput.placeholder}
                        onChange={(e) => setNewInput({ ...newInput, placeholder: e.target.value })}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="ex: Digite seu nome"
                      />
                    </div>

                    {/* Opções do dropdown personalizado */}
                    {newInput.type === 'dropdown' && !inputTypes.find(t => t.value === newInput.type)?.presetOptions && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Opções da Lista
                        </label>
                        <div className="space-y-2">
                          {(newInput.options || []).map((option, idx) => (
                            <div key={idx} className="flex gap-2">
                              <input
                                type="text"
                                value={option.value}
                                onChange={(e) => updateDropdownOption(idx, 'value', e.target.value)}
                                className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Valor"
                              />
                              <input
                                type="text"
                                value={option.label}
                                onChange={(e) => updateDropdownOption(idx, 'label', e.target.value)}
                                className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="Label"
                              />
                              <button
                                onClick={() => removeDropdownOption(idx)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={addDropdownOption}
                            className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                          >
                            + Adicionar Opção
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Campo obrigatório */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="required"
                    checked={newInput.required}
                    onChange={(e) => setNewInput({ ...newInput, required: e.target.checked })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="required" className="text-sm font-medium text-gray-700">
                    Campo obrigatório
                  </label>
                </div>

                {/* Botões de ação */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAddInput(false);
                      setShowAdvancedSettings(false);
                      setNewInput({
                        type: 'text',
                        name: '',
                        label: '',
                        placeholder: '',
                        required: false,
                        options: []
                      });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddInput}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Adicionar Campo
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddInput(true)}
              className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-xl text-green-600 hover:border-indigo-400 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 font-semibold"
            >
              <Plus className="w-5 h-5" />
              Adicionar Campo Personalizado
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default FormBuilder;